import type { SiteRagReport } from '../../types/siteRag';

/** Screen evidence only: equipment duty is a different water boundary. */
export function waterScreenText(report: SiteRagReport): string {
  const screen = report.waterAnalysis;
  if (!screen) return '';
  return [
    screen.supplyAvailability,
    screen.groundwaterAssessment,
    screen.surfaceWaterAssessment,
    screen.waterRightsAnalysis,
    screen.droughtResilience,
    screen.consumptiveUse?.withdrawalPermit,
    screen.consumptiveUse?.seniorRightCurtailment,
    ...(screen.risks ?? []).filter((risk) => risk.evidenceState === 'confirmed').map((risk) => risk.claim),
  ].filter((value): value is string => typeof value === 'string').join('\n');
}

/** Explicit affirmative denial, never an unknown question or a negated denial. */
export function waterScreenHasDeniedPermit(report: SiteRagReport): boolean {
  // A typed permit field may contain only its disposition ("Denied"). Supply
  // the field's subject without asking generated prose to repeat that label.
  const typedPermit = report.waterAnalysis?.consumptiveUse?.withdrawalPermit;
  const text = [waterScreenText(report), typedPermit ? `Withdrawal permit: ${typedPermit}` : ''].join('\n');
  return (text.match(/[^.!?;\n]+[.!?;]?/g) ?? []).some((sentence) => {
    if (/\?\s*$/.test(sentence)) return false;
    // Scope uncertainty to the denial's clause. An appeal that *may* happen
    // after a definite denial does not make the denial itself uncertain.
    return sentence.split(/,\s*(?:but|and|although|however)\b/i).some((clause) => {
      const assertion = /\bwithdrawal permit\s*(?::\s*|\s+(?:(?:is|was|has been|has now been)\s+)?)(?:denied|refused|blocked)\b/i.exec(clause)
        ?? /\b(?:denied|refused|blocked)\s+(?:(?:the|a|our|its)\s+)?withdrawal permit\b/i.exec(clause);
      if (!assertion) return false;
      // Only modifiers BEFORE the assertion can negate/qualify it; a later
      // action such as "could appeal" is a separate proposition.
      const prefix = clause.slice(0, assertion.index);
      return !/\b(?:not|never|no evidence|whether|if|unless|could|may|might|risk of)\b/i.test(prefix);
    });
  });
}
